<template>
  <div>
    <validation-observer
      v-slot="specificItemFormObserver"
      slim
    >
      <form-section-highlight>
        <form-row :label="label" />

        <!-- ITEM TYPE -->
        <form-row
          :id="`content_${uniqueId}_specificitem_property-specific-items-item`"
          question="What type of item is it?"
        >
          <validation-provider
            v-slot="{ errors, failed }"
            name="Property Specific Items Item"
            :vid="`content_${uniqueId}_specificitem_property-specific-items-item`"
            rules="propertySpecificItems"
            slim
          >
            <qz-select
              v-model="currentItem.propertySpecificItemsItem"
              :items="options.propertySpecificItemsItemItems"
              placeholder="Please select type"
              data-test="property_specific_items_item"
              :error="failed"
              :error-messages="errors"
            />
          </validation-provider>
        </form-row>

        <!-- ITEM DESCRIPTION -->
        <form-row
          :id="`content_${uniqueId}_specificitem_property-specific-items-description`"
          question="Please enter a short description for this item"
        >
          <validation-provider
            v-slot="{ errors, failed }"
            name="Property Specific Items Description"
            :vid="`content_${uniqueId}_specificitem_property-specific-items-description`"
            rules="propertyInsuredItemDescription"
            slim
          >
            <qz-textarea
              v-model.trim="currentItem.propertySpecificItemsDescription"
              placeholder="Enter a short description for only one item"
              :error="failed"
              data-test="property_specific_items_description"
              :maxlength="inputCharsLimiters.ITEM_DESCRIPTION_MAX"
              :error-messages="errors"
              @keydown.enter.prevent
              @keyup="applyCharLimiterAndRemoveNewLines"
            />
          </validation-provider>
        </form-row>

        <!-- ITEM VALUE -->
        <form-row
          :id="`content_${uniqueId}_specificitem_property-specific-items-value`"
          question="Please give a value to this item"
          :error="formObserver.validated && !specificItemFormObserver.valid"
        >
          <validation-provider
            v-slot="{ errors, failed }"
            name="Property Specific Items Value"
            :vid="`content_${uniqueId}_specificitem_property-specific-items-value`"
            rules="specificItemValue"
            slim
          >
            <currency-input
              v-model.number="currentItem.propertySpecificItemsValue"
              hide-details="auto"
              :error="failed"
              outlined
              data-test="property_specific_items_value"
              :error-messages="errors"
              placeholder="e.g. 1000"
              :maxlength="inputCharsLimiters.INSURED_ITEM_MAX_VALUE"
              prefix="£"
            />
          </validation-provider>

          <template #helpText>
            <p>
              This is the amount it would cost to replace your item if you had to buy it new today.
            </p>
          </template>
        </form-row>

        <!-- ITEM COVER -->
        <form-row
          :id="`content_${uniqueId}_specificitem_property-specific-items-outside`"
          question="Would you like to add cover to this item
for outside the home?"
        >
          <validation-provider
            v-slot="{ failed }"
            name="Property Specific Items Outside"
            :vid="`content_${uniqueId}_specificitem_property-specific-items-outside`"
            rules="required"
            slim
          >
            <radio
              v-model="currentItem.propertySpecificItemsOutside"
              :name="`${uniqueId}-property-specific-items-outside`"
              wide
              data-test="property_specific_items_outside"
              :error="failed"
              :items="options.yesNo"
            />
          </validation-provider>
        </form-row>

        <!-- SUBMIT BTN -->
        <div class="row no-gutters justify-end mt-4">
          <qz-btn
            depressed
            x-large
            dark
            rounded
            color="navy-light"
            data-test="save_details_specific_item_btn"
            @click="saveItem(specificItemFormObserver)"
          >
            Save details
            <v-icon right>
              mdi-content-save
            </v-icon>
          </qz-btn>
          <qz-btn
            color="primary"
            text
            rounded
            class="ml-2"
            x-large
            data-test="cancel_specific_item_btn"
            @click.prevent="clearItem"
          >
            Cancel
            <template v-if="isEdit">
              edit
            </template>
          </qz-btn>
        </div>
      </form-section-highlight>
    </validation-observer>

    <!-- HIDDEN FIELD TO CHECK EDITOR NOT OPENED -->
    <validation-provider
      v-slot="{ failed }"
      name="Form Open Specific Item"
      vid="content_form-open-specific-item"
      :rules="{ is_not: { other: true } }"
    >
      <input
        :value="formOpen"
        type="hidden"
      >
      <div
        v-if="failed && formObserver.validated"
        class="error--text text-body-1 mt-4"
      >
        You have unsaved details. Please save or cancel these details before continuing.
      </div>
    </validation-provider>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash';

import getStepOptions from '@/helpers/home/getStepOptions';
import useSubformValidation from '@/composables/useSubformValidation';

export default {
  name: 'form-specific-item',
  props: {
    uniqueId: {
      type: String,
      default: 'new',
    },
    index: {
      type: Number,
      default: null,
    },
    formObserver: {
      type: Object,
      required: true,
    },
    item: {
      type: Object,
      required: true,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    sectionItemName: {
      type: String,
      required: true,
    },
  },
  inject: [
    'inputCharsLimiters',
    'helpers',
  ],
  data () {
    return {
      formOpen: true,
      currentItem: cloneDeep(this.item),
    };
  },
  computed: {
    label () {
      return this.helpers.getSubformLabel('Item', this.isEdit, this.index);
    },
  },
  methods: {
    async saveItem (observer) {
      await this.saveSubform({
        observer,
        quoteData: this.currentItem,
        subformType: 'specificItemsDetails',
      });
    },
    clearItem () {
      this.$emit('clear');
    },
    applyCharLimiterAndRemoveNewLines () {
      this.helpers.limitInputChars(this.currentItem, 'propertySpecificItemsDescription', this.inputCharsLimiters.ITEM_DESCRIPTION_MAX);
      this.helpers.removeNewLineChars(this.currentItem, 'propertySpecificItemsDescription');
    },
  },
  setup (props, context) {
    return {
      options: getStepOptions(2),
      ...useSubformValidation(props, context),
    };
  },
};
</script>
